import { slugifyShopCategory } from "../templateShopCatalog";
import type { ShopCatalogProduct, ShopCategoryTab } from "../templateShopCatalog";
import { productBrowsePrimarySlug } from "../shopBreadcrumbs";
import type { FeaturedCarouselItem } from "../types";
import {
  clipFeaturedBandTitle,
  type FeaturedBandImageMode,
  type FeaturedBandSlot,
} from "./featuredBandTypes";

function productMatchesCategory(
  product: ShopCatalogProduct,
  label: string,
  slug: string,
): boolean {
  const needle = label.trim().toLowerCase();
  const slugNeedle = slug.trim().toLowerCase();
  if (!slugNeedle && !needle) return false;

  const primarySlug = productBrowsePrimarySlug(product.breadcrumbs);
  const primaryLabel = String(product.breadcrumbs?.primary || "")
    .trim()
    .toLowerCase();
  return (
    (slugNeedle && primarySlug === slugNeedle) ||
    (needle && primaryLabel === needle)
  );
}

function productImagesForCategory(
  products: ShopCatalogProduct[],
  label: string,
  slug: string,
  max: number,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const p of products) {
    if (!productMatchesCategory(p, label, slug)) continue;
    const src = String(p.image || "").trim();
    if (!src || seen.has(src)) continue;
    seen.add(src);
    out.push(src);
    if (out.length >= max) break;
  }
  return out;
}

/**
 * Merge dedicated cover-RPC rows with the browse catalog so keepPreviousData
 * (old slug covers) never blocks painting a newly selected category.
 */
export function mergeFeaturedCategoryCoverPool(args: {
  coverProducts: ShopCatalogProduct[];
  coverBySlug: Map<string, string>;
  catalogProducts: ShopCatalogProduct[];
  primaryCoverBySlug: Map<string, string>;
  coversLoading: boolean;
}): {
  products: ShopCatalogProduct[];
  coverBySlug: Map<string, string> | undefined;
} {
  const {
    coverProducts,
    coverBySlug: dedicated,
    catalogProducts,
    primaryCoverBySlug,
    coversLoading,
  } = args;

  const products =
    coversLoading && !coverProducts.length
      ? []
      : (() => {
          const byId = new Map<string, ShopCatalogProduct>();
          const out: ShopCatalogProduct[] = [];
          const push = (p: ShopCatalogProduct) => {
            const id = String(p.postId || "").trim();
            if (id) {
              if (byId.has(id)) return;
              byId.set(id, p);
            }
            out.push(p);
          };
          for (const p of coverProducts) push(p);
          for (const p of catalogProducts) push(p);
          return out;
        })();

  const coverBySlug = new Map<string, string>();
  if (!coversLoading || dedicated.size) {
    for (const [slug, img] of primaryCoverBySlug) {
      if (slug && img) coverBySlug.set(slug, img);
    }
  }
  for (const [slug, img] of dedicated) {
    if (slug && img) coverBySlug.set(slug, img);
  }

  return {
    products,
    coverBySlug: coverBySlug.size ? coverBySlug : undefined,
  };
}

/** Auto cover URLs for a category slot — shared by canvas preview and configure drawer. */
export function categoryCoverUrls(
  products: ShopCatalogProduct[],
  label: string,
  slug: string,
  opts?: {
    coverBySlug?: Map<string, string>;
    /** Per-primary browse page images (same RPC as Products → category). */
    coverUrlsBySlug?: Map<string, string[]>;
    max?: number;
  },
): string[] {
  const max = opts?.max ?? 4;
  const fromBrowse = (opts?.coverUrlsBySlug?.get(slug) || [])
    .map((u) => String(u || "").trim())
    .filter(Boolean);
  if (fromBrowse.length) {
    return Array.from(
      { length: max },
      (_, j) => fromBrowse[j] || fromBrowse[0] || "",
    );
  }
  // Fallback: scan loaded catalog, then facet sample.
  const urls = productImagesForCategory(
    products,
    label,
    slug,
    Math.max(4, max),
  );
  const facetCover = opts?.coverBySlug?.get(slug)?.trim() || "";
  if (facetCover && !urls.includes(facetCover)) {
    urls.push(facetCover);
  }
  return Array.from({ length: max }, (_, j) => urls[j] || urls[0] || "");
}

function labelForSelectedSlug(
  slug: string,
  products: ShopCatalogProduct[],
): string {
  const want = slug.trim().toLowerCase();
  if (!want) return "Category";
  for (const p of products) {
    if (productBrowsePrimarySlug(p.breadcrumbs) === want) {
      const label = String(p.breadcrumbs?.primary || "").trim();
      if (label) return label;
    }
  }
  // Last resort: readable slug
  return want
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Map saved category slot slugs onto tabs that still exist.
 * Missing / removed categories fall back to the next unused tab.
 *
 * Valid picks are kept even when duplicated — remapping a fresh select onto
 * another slot's category made the first footwear→home click look like a no-op
 * (canvas swapped/kept footwear via dedupe while Update went dirty).
 */
export function resolveFeaturedCategorySlugs(
  selected: string[],
  tabs: Array<{ slug: string; label: string }>,
  count: number,
): string[] {
  // Always clamp — corrupt cfg.count (e.g. 5) must never paint extra tiles.
  const slotCount = Math.min(
    4,
    Math.max(1, Math.round(Number(count)) || 1),
  );
  const available = tabs
    .map((t) => ({
      slug: String(t.slug || "").trim(),
      label: String(t.label || "").trim(),
    }))
    .filter((t) => t.slug);
  if (!available.length) {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of selected) {
      const slug = String(raw || "").trim();
      if (!slug || seen.has(slug)) continue;
      seen.add(slug);
      out.push(slug);
      if (out.length >= slotCount) break;
    }
    return out;
  }

  const bySlug = new Map(available.map((t) => [t.slug, t]));
  const byLabelSlug = new Map(
    available.map((t) => [slugifyShopCategory(t.label), t]),
  );
  const used = new Set<string>();
  const nextUnused = () => available.find((t) => !used.has(t.slug));

  const resolved: string[] = [];

  for (let i = 0; i < selected.length && resolved.length < slotCount; i++) {
    const slug = String(selected[i] || "").trim();
    if (!slug) continue;
    const hit =
      bySlug.get(slug) ||
      byLabelSlug.get(slug) ||
      byLabelSlug.get(slugifyShopCategory(slug));
    if (hit) {
      // Skip duplicates — Quad + two "clothing" picks was painting the same
      // tile twice and leaving a 5th leftover from the raw slug list.
      if (used.has(hit.slug)) continue;
      used.add(hit.slug);
      resolved.push(hit.slug);
      continue;
    }
    // Keep the creator's pick when browse tabs briefly lag the configure
    // dropdown (first Footwear→Travel click). Still de-dupe.
    if (used.has(slug)) continue;
    used.add(slug);
    resolved.push(slug);
  }

  while (resolved.length < slotCount) {
    const fallback = nextUnused();
    if (!fallback) break;
    used.add(fallback.slug);
    resolved.push(fallback.slug);
  }

  return resolved.slice(0, slotCount);
}

/**
 * Build featured-category slots from breadcrumb primary tabs + product covers.
 * Optional `selectedSlugs` (comma-separated or array) filters / orders slots.
 *
 * Slugs that are no longer in `tabs` fall back to the first unused present
 * category so the band never shows a removed category.
 */
export function buildFeaturedCategorySlots(args: {
  tabs: ShopCategoryTab[];
  products: ShopCatalogProduct[];
  count: number;
  mode: FeaturedBandImageMode;
  maxImages: number;
  selectedSlugs?: string[] | string;
  /** Optional RPC facet sample covers keyed by primary slug. */
  coverBySlug?: Map<string, string>;
  /** Per-primary browse covers — matches first products on that category page. */
  coverUrlsBySlug?: Map<string, string[]>;
}): FeaturedBandSlot[] {
  const slotCount = Math.min(
    4,
    Math.max(1, Math.round(Number(args.count)) || 1),
  );
  const { tabs, products, mode, maxImages, coverBySlug, coverUrlsBySlug } =
    args;
  let ordered: ShopCategoryTab[] = [...tabs];
  const raw = args.selectedSlugs;
  const selected = Array.isArray(raw)
    ? raw.map((s) => String(s).trim()).filter(Boolean)
    : String(raw || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

  if (selected.length || tabs.length) {
    const resolved = resolveFeaturedCategorySlugs(selected, tabs, slotCount);
    const bySlug = new Map(tabs.map((t) => [t.slug, t]));
    ordered = resolved.map((slug) => {
      const hit = bySlug.get(slug);
      if (hit) return hit;
      return {
        slug,
        label: labelForSelectedSlug(slug, products),
        count: 0,
      };
    });
  }

  return ordered.slice(0, slotCount).map((tab, i) => {
    const urls = categoryCoverUrls(products, tab.label, tab.slug, {
      coverBySlug,
      coverUrlsBySlug,
      max: 4,
    });
    return {
      id: tab.slug || `cat-${i}`,
      title: clipFeaturedBandTitle(tab.label),
      slug: tab.slug,
      mode,
      count: tab.count,
      images: Array.from({ length: 4 }, (_, j) => ({
        src: urls[j] || urls[0] || "",
        alt: tab.label,
      })),
    };
  });
}

/**
 * Build featured-collection slots from homepage featured carousel items
 * (carousel-collection members) or Recent Content carousels as fallback.
 */
export function buildFeaturedCollectionSlots(args: {
  items: FeaturedCarouselItem[];
  count: number;
  mode: FeaturedBandImageMode;
  maxImages: number;
  selectedIds?: string[] | string;
}): FeaturedBandSlot[] {
  const { items, count, mode, maxImages } = args;
  let ordered = [...items];
  const raw = args.selectedIds;
  const selected = Array.isArray(raw)
    ? raw.map((s) => String(s).trim()).filter(Boolean)
    : String(raw || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

  if (selected.length) {
    const byId = new Map(items.map((it) => [String(it.id), it]));
    const bySlug = new Map(
      items.map((it) => [String(it.slug || "").trim(), it]),
    );
    ordered = selected
      .map((key) => byId.get(key) || bySlug.get(key))
      .filter(Boolean) as FeaturedCarouselItem[];
    if (ordered.length < count) {
      for (const it of items) {
        if (ordered.some((o) => o.id === it.id)) continue;
        ordered.push(it);
        if (ordered.length >= count) break;
      }
    }
  }

  return ordered.slice(0, count).map((it, i) => {
    const cover = String(it.image || it.productImage || "").trim();
    return {
      id: String(it.id || it.slug || `col-${i}`),
      title: clipFeaturedBandTitle(it.title || "Collection"),
      slug: it.slug,
      mode,
      images: Array.from({ length: Math.max(4, maxImages) }, (_, j) => ({
        src: cover,
        alt: it.title || "",
      })).slice(0, 4),
    };
  });
}
